import type { ApiClient, ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';

type KuduClient = Pick<ApiClient, 'invokeKuduMethod'>;

interface RawEnrollmentCourse {
  id?: string | null;
  name?: string | null;
  premium_fee?: number | string | null;
  premiumFee?: number | string | null;
  uid?: string | null;
}

interface RawGetCoursePayload {
  course?: RawEnrollmentCourse | null;
}

interface GetCourseRequest {
  course_id: string;
  full_response: boolean;
}

interface ChargeViaStripeRequest {
  amount: number;
}

interface RawChargeViaStripeResponse {
  ClientSecret?: string | null;
  clientSecret?: string | null;
  error?: string | boolean | null;
}

interface UpdateUserCourseRequest {
  new_course_id: string;
  user_id: string;
}

interface UpdatePaymentCodeRequest {
  course_id: string;
  // The access-code value is sent in the proto `id` field.
  id: string;
  used: boolean;
  user_id: string;
  value_cents: number;
}

interface RawUpdatePaymentCodeResponse {
  succeeded?: boolean | null;
}

export interface EnrollmentCourse {
  id: string;
  name: string;
  premiumFee: number;
}

export interface CourseEnrollmentService {
  addCourseToUser(params: {
    courseId: string;
    userId: string;
  }): Promise<void>;
  lookupCourseForEnrollment(courseId: string): Promise<EnrollmentCourse>;
  redeemCourseAccessCode(params: {
    code: string;
    courseId: string;
    userId: string;
    valueCents: number;
  }): Promise<void>;
  startStripeCoursePayment(amount: number): Promise<string>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeNonNegativeCents(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? Math.round(value) : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0;
  }

  return 0;
}

function createDataError(message: string, status?: number): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  error.status = status;
  return error;
}

function unwrapLegacyEnvelope<T>(
  response: T | LegacyKuduResponseEnvelope<T> | null | undefined,
): T | null {
  if (!response || !isRecord(response)) {
    return null;
  }

  const responseRecord = response as Record<string, unknown>;

  if ('Response' in responseRecord) {
    return unwrapLegacyEnvelope(
      (response as unknown as LegacyKuduResponseEnvelope<T>).Response,
    );
  }

  return response as T;
}

function getRawCourse(
  payload: RawGetCoursePayload | RawEnrollmentCourse | null,
): RawEnrollmentCourse | null {
  if (!payload || !isRecord(payload)) {
    return null;
  }

  if (isRecord(payload.course)) {
    return payload.course as RawEnrollmentCourse;
  }

  if ('id' in payload || 'uid' in payload || 'name' in payload) {
    return payload as RawEnrollmentCourse;
  }

  return null;
}

export function normalizeEnrollmentCourse(
  payload:
    | RawGetCoursePayload
    | RawEnrollmentCourse
    | LegacyKuduResponseEnvelope<RawGetCoursePayload | RawEnrollmentCourse>
    | null
    | undefined,
  fallbackCourseId: string,
): EnrollmentCourse | null {
  const unwrapped = unwrapLegacyEnvelope<
    RawGetCoursePayload | RawEnrollmentCourse
  >(payload);
  const rawCourse = getRawCourse(unwrapped);

  if (!rawCourse) {
    return null;
  }

  const id =
    normalizeString(rawCourse.id) ??
    normalizeString(rawCourse.uid) ??
    fallbackCourseId;
  const name = normalizeString(rawCourse.name) ?? 'Untitled course';

  return {
    id,
    name,
    premiumFee: normalizeNonNegativeCents(
      rawCourse.premium_fee ?? rawCourse.premiumFee,
    ),
  };
}

function normalizeClientSecret(
  response:
    | RawChargeViaStripeResponse
    | LegacyKuduResponseEnvelope<RawChargeViaStripeResponse>
    | null
    | undefined,
): string | null {
  const payload = unwrapLegacyEnvelope<RawChargeViaStripeResponse>(response);

  if (!payload || payload.error) {
    return null;
  }

  return (
    normalizeString(payload.ClientSecret) ??
    normalizeString(payload.clientSecret)
  );
}

export function createCourseEnrollmentService(
  client: KuduClient,
): CourseEnrollmentService {
  return {
    async addCourseToUser({ courseId, userId }) {
      await client.invokeKuduMethod<unknown, UpdateUserCourseRequest>(
        'UpdateUser',
        {
          new_course_id: courseId,
          user_id: userId,
        },
      );
    },
    async redeemCourseAccessCode({ code, courseId, userId, valueCents }) {
      const response = await client.invokeKuduMethod<
        | RawUpdatePaymentCodeResponse
        | LegacyKuduResponseEnvelope<RawUpdatePaymentCodeResponse>,
        UpdatePaymentCodeRequest
      >('UpdatePaymentCode', {
        course_id: courseId,
        id: code,
        used: true,
        user_id: userId,
        value_cents: valueCents,
      });
      const payload =
        unwrapLegacyEnvelope<RawUpdatePaymentCodeResponse>(response);

      if (!payload?.succeeded) {
        throw createDataError('That access code could not be redeemed.');
      }
    },
    async lookupCourseForEnrollment(courseId) {
      const response = await client.invokeKuduMethod<
        | RawGetCoursePayload
        | RawEnrollmentCourse
        | LegacyKuduResponseEnvelope<RawGetCoursePayload | RawEnrollmentCourse>,
        GetCourseRequest
      >('GetCourse', {
        course_id: courseId,
        full_response: true,
      });
      const course = normalizeEnrollmentCourse(response, courseId);

      if (!course) {
        throw createDataError('Course not found.', 404);
      }

      return course;
    },
    async startStripeCoursePayment(amount) {
      const response = await client.invokeKuduMethod<
        | RawChargeViaStripeResponse
        | LegacyKuduResponseEnvelope<RawChargeViaStripeResponse>,
        ChargeViaStripeRequest
      >('ChargeViaStripe', { amount });
      const clientSecret = normalizeClientSecret(response);

      if (!clientSecret) {
        throw createDataError('Unable to start card payment.');
      }

      return clientSecret;
    },
  };
}
